/**
 * Legacy CMS content frequently ships inline styles that reference the old
 * `MarkGEO` / `MarkGEOCAPS` (and the interim `BOG Headline`) font families.
 * Those must render with the current Bank of Georgia font instead:
 *   MarkGEO                     -> BOG
 *   MarkGEOCAPS / BOG Headline  -> BOG + uppercase rendering
 *
 * The old caps display fonts baked the uppercasing into the glyphs, so a bare
 * family swap would lose that look. Wherever a caps family is used as a
 * `font-family` value we swap it to `BOG` and inject `text-transform: uppercase`
 * and `font-feature-settings: 'case'` into the same declaration block so the
 * caps styling is preserved.
 *
 * We rewrite the raw HTML string before it reaches `unsafeHTML`, so any content
 * coming from CMS html fields is normalized in one place.
 *
 * Ordering note: `MarkGEOCAPS` contains `MarkGEO`, so the caps families are
 * handled first. `\b` word boundaries also keep the `MarkGEO` rule from
 * touching `MarkGEOCAPS` and from matching partial tokens inside identifiers.
 */

// A `font-family` declaration whose value is a caps display font, up to the
// declaration delimiter (`;`, `}`, quote or end). The whole declaration is
// swapped for `BOG` plus the two rules that restore the uppercase look.
const CAPS_FONT_FAMILY =
  /font-family\s*:\s*['"]?(?:MarkGEOCAPS|BOG Headline)['"]?[^;}"']*;?/g;

const CAPS_FONT_FAMILY_REPLACEMENT =
  `font-family: 'BOG'; text-transform: uppercase; font-feature-settings: 'case';`;

const FONT_REPLACEMENTS = [
    // 1) Caps display fonts used as a font-family value -> BOG + uppercase look.
    [CAPS_FONT_FAMILY, CAPS_FONT_FAMILY_REPLACEMENT],
    // 2) Any leftover bare caps tokens (outside a font-family declaration) -> BOG.
    [/\bMarkGEOCAPS\b/g, 'BOG'],
    [/\bBOG Headline\b/g, 'BOG'],
    // 3) Plain body font -> BOG.
    [/\bMarkGEO\b/g, 'BOG'],
];

/**
 * Replace legacy font-family names with their Bank of Georgia equivalents.
 * Non-string values are returned untouched so callers can pass through safely.
 * @param {*} value raw HTML (or any value)
 * @return {*} value with legacy font names replaced
 */
export function replaceLegacyFonts(value) {
    if (typeof value !== 'string') {
        return value;
    }
    return FONT_REPLACEMENTS.reduce(
        (result, [pattern, replacement]) => result.replace(pattern, replacement),
        value,
    );
}
